import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, ArrowRight, Gift } from "lucide-react";
import { IMG, LOGO_MAIN } from "@/data/content";

const WA_NUMBER = "40740433894";
const WA_MESSAGE =
  "Salut DOTAPAN! Am intrat pe site și vreau să discut despre cele mai bune condiții B2B pentru afacerea mea HoReCa (produse, preț, livrare).";
const SESSION_KEY = "dotapan_exit_popup_shown";
const INACTIVITY_MS = 60000; // mobile fallback

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const triggeredRef = useRef(false);
  const mobileTimerRef = useRef(null);

  const trigger = () => {
    if (triggeredRef.current) return;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1") return;
    triggeredRef.current = true;
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {}
    setOpen(true);
  };

  useEffect(() => {
    // Desktop: mouse leaves top of viewport
    const onMouseLeave = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };

    // Mobile fallback: show after inactivity if nothing has been submitted
    const resetIdle = () => {
      if (mobileTimerRef.current) clearTimeout(mobileTimerRef.current);
      mobileTimerRef.current = setTimeout(trigger, INACTIVITY_MS);
    };
    const isTouch = window.matchMedia("(hover: none)").matches;

    if (isTouch) {
      // Start mobile timer after 8s minimum on-page
      const startTimer = setTimeout(resetIdle, 8000);
      ["scroll", "touchstart", "click"].forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }));
      return () => {
        clearTimeout(startTimer);
        if (mobileTimerRef.current) clearTimeout(mobileTimerRef.current);
        ["scroll", "touchstart", "click"].forEach((ev) => window.removeEventListener(ev, resetIdle));
      };
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
      return () => document.removeEventListener("mouseleave", onMouseLeave);
    }
  }, []);

  // Body scroll lock
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const goWhatsApp = () => {
    const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_MESSAGE)}`;
    window.open(href, "_blank", "noopener,noreferrer");
    setTimeout(() => setOpen(false), 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex items-center justify-center px-4"
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={() => setOpen(false)}
            data-testid="exit-intent-overlay"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.4, ease: [0.22, 0.72, 0.21, 1] }}
            className="relative w-full max-w-lg rounded-3xl overflow-hidden bg-[#0b0b0f] border border-gold/40 shadow-[0_40px_100px_-20px_rgba(217,164,65,0.45)]"
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Închide"
              data-testid="exit-intent-close"
              className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full bg-black/60 border border-white/15 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Hero strip with product image */}
            <div className="relative h-44 overflow-hidden">
              <img
                src={IMG.croissantArtisan}
                alt="Croissante DOTAPAN"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0f] via-[#0b0b0f]/70 to-transparent" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0b0b0f]/80 border border-gold/40 backdrop-blur text-gold">
                <Gift className="h-3.5 w-3.5" />
                <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">Ofertă specială B2B</span>
              </div>
              <img
                src={LOGO_MAIN}
                alt="DOTAPAN"
                className="absolute bottom-3 right-4 h-10 object-contain opacity-90"
              />
            </div>

            <div className="p-6 md:p-8 text-center">
              <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                Stai puțin! Hai să-ți facem <br />
                <em className="text-gold not-italic">cea mai bună ofertă B2B.</em>
              </h3>
              <p className="mt-4 text-sm md:text-base text-white/80 leading-relaxed">
                Vorbim direct pe WhatsApp: îți trimitem catalogul actualizat, prețuri B2B, mostre și condiții
                preferențiale de livrare pentru afacerea ta HoReCa.
              </p>

              <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-[0.18em] text-white/70">
                <div className="px-2 py-2 rounded-full border border-gold/30 bg-gold/5">Prețuri B2B</div>
                <div className="px-2 py-2 rounded-full border border-gold/30 bg-gold/5">Mostre gratis</div>
                <div className="px-2 py-2 rounded-full border border-gold/30 bg-gold/5">Livrare rapidă</div>
              </div>

              <button
                onClick={goWhatsApp}
                data-testid="exit-intent-whatsapp"
                className="mt-6 inline-flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#25d366] text-white font-semibold text-sm shadow-[0_15px_40px_-10px_rgba(37,211,102,0.6)] hover:brightness-110 hover:-translate-y-0.5 transition-all"
              >
                <MessageCircle className="h-4 w-4" />
                Cere oferta pe WhatsApp
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="exit-intent-dismiss"
                className="mt-3 text-[11px] uppercase tracking-[0.2em] text-white/45 hover:text-white/80"
              >
                Nu acum, mulțumesc
              </button>
              <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-gold">+40 740 433 894 • dotapanromania@gmail.com</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
